import { Layout, Menu, Input } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  SettingOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  SwapOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  FundOutlined,
  BarChartOutlined,
  BankOutlined,
  SolutionOutlined,
  GlobalOutlined,
  FolderOutlined,
  ProjectOutlined,
  CompassOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import type { MenuProps } from 'antd';
import useThemeStore from '../../store/themeStore';
import { colors } from '../../theme/tokens';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

interface SidebarProps {
  collapsed: boolean;
}

export default function Sidebar({ collapsed }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useThemeStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const menuItems: MenuItem[] = [
    {
      key: '/navigation',
      icon: <CompassOutlined />,
      label: 'Navigation Hub',
      style: {
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        marginBottom: 8,
        borderRadius: 4,
      },
    },
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'DASHBOARD',
      children: [
        {
          key: '/',
          label: 'Overview',
        },
        {
          key: '/dashboard/consolidated',
          label: 'Consolidated (Multi-Company)',
        },
      ],
    },
    {
      key: 'transactions',
      icon: <SwapOutlined />,
      label: 'TRANSACTIONS',
      children: [
        {
          key: '/accounting/journal-entries',
          label: 'Journal Entries',
        },
        {
          key: '/transactions/cash-ledger',
          label: 'Cash Ledger',
        },
        {
          key: '/transactions/reconciliation-rules',
          label: 'Reconciliation Rules',
        },
      ],
    },
    {
      key: 'banking',
      icon: <BankOutlined />,
      label: 'BANKING',
      children: [
        {
          key: '/banking/accounts',
          label: 'Bank Accounts',
        },
        {
          key: '/banking/reconciliation',
          label: 'Reconciliation',
        },
        {
          key: '/banking/statements',
          label: 'Statements',
        },
        {
          key: '/banking/transfers',
          label: 'Transfers',
        },
      ],
    },
    {
      key: 'sales',
      icon: <ShoppingOutlined />,
      label: 'SALES',
      children: [
        {
          key: '/customers',
          label: 'Customers',
        },
        {
          key: '/quotes',
          label: 'Quotes',
        },
        {
          key: '/sales-orders',
          label: 'Sales Orders',
        },
        {
          key: '/invoices',
          label: 'Invoices',
        },
        {
          key: '/payments/record',
          label: 'Payments In',
        },
        {
          key: '/sales-orders/fulfillment',
          label: 'Fulfillment',
        },
        {
          key: '/sales-orders/shipping',
          label: 'Shipping',
        },
        {
          key: '/rmas',
          label: 'RMA (Returns)',
        },
      ],
    },
    {
      key: 'purchases',
      icon: <ShoppingCartOutlined />,
      label: 'PURCHASES',
      children: [
        {
          key: '/vendors',
          label: 'Vendors',
        },
        {
          key: '/purchase-orders',
          label: 'Purchase Orders',
        },
        {
          key: '/expenses',
          label: 'Bills/Expenses',
        },
        {
          key: '/payments/out',
          label: 'Payments Out',
        },
        {
          key: '/purchase-orders/receiving',
          label: 'Receiving',
        },
        {
          key: '/purchase-orders/approvals',
          label: 'Approvals',
        },
      ],
    },
    {
      key: 'inventory',
      icon: <AppstoreOutlined />,
      label: 'INVENTORY',
      children: [
        {
          key: '/inventory/dashboard',
          label: 'Dashboard',
        },
        {
          key: '/inventory/items',
          label: 'Items',
        },
        {
          key: '/inventory/movements',
          label: 'Movements',
        },
        {
          key: '/inventory/cycle-count',
          label: 'Cycle Count',
        },
        {
          key: '/inventory/serial-numbers',
          label: 'Serial Numbers',
        },
        {
          key: '/inventory/lot-batch',
          label: 'Lot/Batch Tracking',
        },
        {
          key: '/inventory/locations',
          label: 'Locations',
        },
        {
          key: '/inventory/transfers',
          label: 'Transfer Orders',
        },
      ],
    },
    {
      key: 'manufacturing',
      icon: <ToolOutlined />,
      label: 'MANUFACTURING',
      children: [
        {
          key: '/bom',
          label: 'Bill of Materials (BOM)',
        },
        {
          key: '/assembly',
          label: 'Work Orders',
        },
        {
          key: '/bom/cost-analysis',
          label: 'Cost Analysis',
        },
      ],
    },
    {
      key: 'planning',
      icon: <FundOutlined />,
      label: 'PLANNING',
      children: [
        {
          key: '/demand-planning',
          label: 'Dashboard',
        },
        {
          key: '/demand-planning/forecast',
          label: 'Demand Forecasting',
        },
        {
          key: '/demand-planning/reorder-points',
          label: 'Reorder Points',
        },
        {
          key: '/demand-planning/safety-stock',
          label: 'Safety Stock',
        },
        {
          key: '/demand-planning/requisitions',
          label: 'Purchase Requisitions',
        },
        {
          key: '/demand-planning/suggestions',
          label: 'Suggestions',
        },
      ],
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'REPORTS',
      children: [
        {
          key: 'reports-financial',
          label: 'Financial',
          type: 'group',
          children: [
            {
              key: '/reports/trial-balance',
              label: 'Trial Balance',
            },
            {
              key: '/reports/income-statement',
              label: 'Income Statement (P&L)',
            },
            {
              key: '/reports/balance-sheet',
              label: 'Balance Sheet',
            },
            {
              key: '/reports/cash-flow',
              label: 'Cash Flow',
            },
            {
              key: '/reports/account-activity',
              label: 'Account Activity',
            },
          ],
        },
        {
          key: 'reports-arap',
          label: 'AR/AP',
          type: 'group',
          children: [
            {
              key: '/reports/aging',
              label: 'Aging Reports',
            },
            {
              key: '/reports/customer-statements',
              label: 'Customer Statements',
            },
            {
              key: '/reports/vendor-statements',
              label: 'Vendor Statements',
            },
          ],
        },
        {
          key: 'reports-inventory',
          label: 'Inventory',
          type: 'group',
          children: [
            {
              key: '/reports/inventory?type=VALUATION',
              label: 'Valuation',
            },
            {
              key: '/reports/inventory?type=MOVEMENT',
              label: 'Movement Reports',
            },
            {
              key: '/reports/inventory?type=STOCK_LEVELS',
              label: 'Stock Reports',
            },
          ],
        },
        {
          key: 'reports-management',
          label: 'Management',
          type: 'group',
          children: [
            {
              key: '/reports/builder',
              label: 'Custom Report Builder',
            },
            {
              key: '/reports/scheduled',
              label: 'Scheduled Reports',
            },
            {
              key: '/reports/history',
              label: 'Report History',
            },
          ],
        },
      ],
    },
    {
      key: 'payroll',
      icon: <SolutionOutlined />,
      label: 'PAYROLL',
      children: [
        {
          key: '/payroll/employees',
          label: 'Employees',
        },
        {
          key: '/payroll/payroll-runs',
          label: 'Payroll Runs',
        },
        {
          key: '/payroll/timesheet-entry',
          label: 'Timesheet Entry',
        },
        {
          key: '/payroll/timesheet-approval',
          label: 'Timesheet Approval',
        },
        {
          key: '/payroll/tax-setup',
          label: 'Tax Setup',
        },
        {
          key: '/payroll/pay-stubs',
          label: 'Pay Stubs',
        },
        {
          key: '/payroll/reports',
          label: 'Payroll Reports',
        },
      ],
    },
    {
      key: 'projects',
      icon: <ProjectOutlined />,
      label: 'PROJECTS',
      children: [
        {
          key: '/projects/dashboard',
          label: 'Project Dashboard',
        },
        {
          key: '/projects',
          label: 'All Projects',
        },
        {
          key: '/projects/time-entries',
          label: 'Time Tracking',
        },
      ],
    },
    {
      key: 'documents',
      icon: <FolderOutlined />,
      label: 'DOCUMENTS',
      children: [
        {
          key: '/documents',
          label: 'Repository',
        },
        {
          key: '/receipts',
          label: 'Receipts',
        },
        {
          key: '/receipts/scanner',
          label: 'Receipt Scanner',
        },
        {
          key: '/documents/templates',
          label: 'Templates',
        },
      ],
    },
    {
      key: 'company-settings',
      icon: <SettingOutlined />,
      label: 'COMPANY SETTINGS',
      children: [
        {
          key: '/accounting/chart-of-accounts',
          label: 'Chart of Accounts',
        },
        {
          key: '/settings/fiscal-periods',
          label: 'Fiscal Periods',
        },
        {
          key: '/settings/tax-rules',
          label: 'Tax Rules',
        },
        {
          key: '/settings/regional',
          label: 'Regional Settings',
        },
        {
          key: '/settings/templates',
          label: 'Templates',
        },
        {
          key: '/settings/integrations',
          label: 'Integrations',
        },
      ],
    },
    {
      key: 'multi-company',
      icon: <GlobalOutlined />,
      label: 'MULTI-COMPANY',
      children: [
        {
          key: '/organizations',
          label: 'Company List',
        },
        {
          key: '/organizations/groups',
          label: 'Organization Groups',
        },
        {
          key: '/users?view=organizations',
          label: 'User Assignments',
        },
        {
          key: '/organizations/consolidation',
          label: 'Consolidation',
        },
        {
          key: '/organizations/intercompany',
          label: 'Intercompany Transactions',
        },
        {
          key: '/organizations/comparison',
          label: 'Comparison',
        },
        {
          key: '/organizations/cash-flow',
          label: 'Group Cash Flow',
        },
      ],
    },
    {
      key: 'administration',
      icon: <SafetyOutlined />,
      label: 'ADMINISTRATION',
      children: [
        {
          key: '/users',
          label: 'Users & Roles',
        },
        {
          key: '/settings/security',
          label: 'Security Settings',
        },
        {
          key: '/admin/audit-logs',
          label: 'Audit Logs',
        },
        {
          key: 'api-management',
          label: 'API Management',
          type: 'group',
          children: [
            {
              key: '/api-management/keys',
              label: 'API Keys',
            },
            {
              key: '/api-management/rate-limits',
              label: 'Rate Limiting',
            },
            {
              key: '/api-management/webhooks',
              label: 'Webhooks',
            },
          ],
        },
        {
          key: 'performance',
          label: 'Performance',
          type: 'group',
          children: [
            {
              key: '/performance/cache',
              label: 'Cache Management',
            },
            {
              key: '/performance/archival',
              label: 'Data Archival',
            },
            {
              key: '/performance/analytics',
              label: 'Usage Analytics',
            },
          ],
        },
        {
          key: '/admin/multi-region',
          label: 'Multi-Region',
        },
        {
          key: '/admin/data-retention',
          label: 'Data Retention',
        },
      ],
    },
  ];

  // Filter menu items based on search query
  const filterMenuItems = (items: MenuItem[], query: string): MenuItem[] => {
    if (!query.trim()) return items;

    const lowerQuery = query.toLowerCase();

    return items
      .map((item: any) => {
        // Check if item label matches
        const labelMatches = item.label?.toString().toLowerCase().includes(lowerQuery);

        // Check children recursively
        let filteredChildren: any[] = [];
        if (item.children) {
          filteredChildren = filterMenuItems(item.children, query);
        }

        // Include item if label matches or has matching children
        if (labelMatches || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : item.children,
          };
        }

        return null;
      })
      .filter(Boolean) as MenuItem[];
  };

  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems, searchQuery),
    [searchQuery, menuItems]
  );

  // Find which sections should be open based on current path
  const findOpenKeysForPath = (path: string, items: MenuItem[]): string[] => {
    const keys: string[] = [];

    const findInItems = (menuItems: MenuItem[], parentKey?: string): boolean => {
      for (const item of menuItems) {
        if (!item || !('key' in item) || item.key === undefined) continue;

        const itemKey = String(item.key);

        if (itemKey === path) {
          if (parentKey) keys.push(parentKey);
          return true;
        }

        if ('children' in item && item.children) {
          if ('type' in item && item.type === 'group') {
            if (findInItems(item.children as MenuItem[], parentKey)) {
              keys.push(itemKey);
              return true;
            }
          } else {
            if (findInItems(item.children as MenuItem[], itemKey)) {
              return true;
            }
          }
        }
      }
      return false;
    };

    findInItems(items);
    return keys;
  };

  // Auto-open section containing current page
  useEffect(() => {
    if (!searchQuery.trim()) {
      const keysForPath = findOpenKeysForPath(location.pathname, menuItems);
      if (keysForPath.length > 0) {
        setOpenKeys(keysForPath);
      }
    }
  }, [location.pathname, searchQuery]);

  // Auto-open filtered groups when searching
  const searchOpenKeys = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }
    // Open all parent groups when searching
    return filteredMenuItems
      .filter((item: any) => item.children && item.children.length > 0)
      .map((item: any) => item.key as string);
  }, [searchQuery, filteredMenuItems]);

  // Use search open keys when searching, otherwise use controlled open keys
  const effectiveOpenKeys = searchQuery.trim() ? searchOpenKeys : openKeys;

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={280}
      collapsedWidth={80}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        background: resolvedTheme === 'dark' ? colors.dark.bg : colors.light.bg,
        borderRight: resolvedTheme === 'dark' ? `1px solid ${colors.dark.borderSecondary}` : `1px solid ${colors.light.borderSecondary}`,
      }}
    >
      <div
        style={{
          height: 64,
          margin: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: resolvedTheme === 'dark' ? colors.dark.text : colors.light.text,
          fontSize: collapsed ? 18 : 20,
          fontWeight: 'bold',
        }}
      >
        {collapsed ? 'GGB' : 'Go Green Book'}
      </div>

      {!collapsed && (
        <div style={{ padding: '0 16px 16px' }}>
          <Input
            placeholder="Search navigation..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{
              borderRadius: 4,
            }}
          />
        </div>
      )}

      <Menu
        theme={resolvedTheme}
        mode="inline"
        selectedKeys={[location.pathname]}
        openKeys={effectiveOpenKeys}
        onOpenChange={(keys) => setOpenKeys(keys as string[])}
        items={filteredMenuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </Sider>
  );
}
